import styled from "@emotion/styled";
import { Button, Dialog, LinearProgress } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import type { FileError, FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";

import { useFormatMessage } from "@skilitics/localization";

import { FileDropzoneComponent } from "./FileDropzoneComponent";

const DEFAULT_MAXIMUM_FILE_SIZE = 500 * 1024 * 1024;

const formatMaximumFileSize = (size: number) => {
  if (size === 0) return "0 Bytes";

  const postfix = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(size) / Math.log(1024));

  return parseFloat((size / Math.pow(1024, i)).toFixed(0)) + " " + postfix[i];
};

const formatStrings: Record<FileError["code"], { [lang: string]: string }> = {
  "file-invalid-type": {
    en: "The file type is not valid.",
  },
  "file-too-large": {
    en: "The maximum file size is { maximumFileSize}.",
  },
  "file-too-small": {
    en: "Minimum file size not reached.",
  },
  "too-many-files": {
    en: "Too many files selected.",
  },
};

export type MediaType = "application" | "audio" | "image" | "text" | "video" | "model";

const mapMediaTypesToAcceptedFileTypes = (mediaTypes: MediaType[] | undefined) => {
  if (mediaTypes) return Object.fromEntries(mediaTypes.map((mediaType) => [`${mediaType}/*`, []]));
  return { "*": [] };
};

export type FileDropzoneProps = {
  children?: ReactNode;
  dataTest?: string;
  empty?: boolean;
  maximumFileSize?: number;
  mediaTypes?: MediaType[];
  mimeType?: string;
  typeLabel?: string;

  onFileSelected?: (file: File) => Promise<void> | void;
  onFileDeselected: () => void;
  onFileSelectionCancelled: () => void;
  onFileProcessed?: () => void;
  onFileRejected?: (details: { reason: string; onHandled: () => void }) => void;
};

export const FileDropzone = ({
  children,
  dataTest,
  empty = !children,
  maximumFileSize = DEFAULT_MAXIMUM_FILE_SIZE,
  mediaTypes,
  mimeType,
  onFileDeselected,
  onFileSelected,
  onFileSelectionCancelled,
  onFileProcessed,
  onFileRejected,
  typeLabel = "file",
}: FileDropzoneProps) => {
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const mapErrorCodeToMessage = useFormatMessage(formatStrings);

  const handleDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      let handled = false;

      const reason = mapErrorCodeToMessage(rejectedFiles[0].errors[0].code, {
        maximumFileSize: formatMaximumFileSize(maximumFileSize),
      });

      const onHandled = () => {
        setErrorMessage(undefined);
        handled = true;
      };

      onFileRejected?.({ reason, onHandled });

      if (handled) return;

      setErrorMessage(reason);
    },
    [mapErrorCodeToMessage, maximumFileSize, onFileRejected],
  );

  const handleDropAccepted = useCallback(
    async (files: File[]) => {
      try {
        setErrorMessage(undefined);
        setProcessing(true);
        await onFileSelected?.(files[0]);
        setProcessing(false);
        onFileProcessed?.();
      } catch (err) {
        if (err instanceof Error && (err.name === "AbortError" || err.name === "CanceledError")) {
          onFileDeselected();
          return;
        }

        console.error(err);
        setErrorMessage(err instanceof Error ? err.message : JSON.stringify(err));
      }
    },
    [onFileSelected, onFileProcessed, onFileDeselected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
    accept:
      mimeType === "all" ? undefined : mimeType ? { [mimeType]: [] } : mapMediaTypesToAcceptedFileTypes(mediaTypes),
    multiple: false,
    maxFiles: 1,
    maxSize: maximumFileSize,
    // Need to remove the existing file before selecting a new one.
    disabled: !empty || processing,
    // Further, you need to click the error icon to see the message,
    noClick: !!errorMessage,
  });

  // derive FileDropzoneComponent visual mode from state.
  // error always wins, but otherwise more transient state wins.
  const mode = errorMessage
    ? "error"
    : processing
      ? "processing"
      : isDragActive
        ? "dragging"
        : empty
          ? "empty"
          : "selected";

  const handleDismiss = useCallback(() => {
    setErrorMessage(undefined);
    onFileSelectionCancelled();
    setProcessing(false);
  }, [onFileSelectionCancelled]);

  return (
    <>
      {empty && !isDragActive && <input {...getInputProps()} data-test={`${dataTest ?? "file-drop-zone"}-input`} />}
      <div {...getRootProps()}>
        <FileDropzoneComponent
          mode={mode}
          typeLabel={typeLabel}
          errorMessage={errorMessage}
          onCancel={onFileSelectionCancelled}
          onDismiss={handleDismiss}
          onRemove={onFileDeselected}
          dataTest={dataTest}
        >
          {children}
        </FileDropzoneComponent>
      </div>

      {mode === "processing" && <UploadingDialog onCancel={handleDismiss} />}
    </>
  );
};

const UploadingDialogContent = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 6px;
`;

// NOTE: This is a temporary solution to prevent users from unmounting the file upload dialog.
const UploadingDialog = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <Dialog open hideBackdrop slotProps={{ paper: { style: { position: "absolute", bottom: "0", right: "0" } } }}>
      <UploadingDialogContent>
        Uploading...
        <LinearProgress style={{ width: 100 }} />
        <Button variant="outlined" onClick={onCancel} size="small">
          Cancel
        </Button>
      </UploadingDialogContent>
    </Dialog>
  );
};
